import Checkbox from "~/common/components/Checkbox";
import type { QuoteLineMO } from "../models";
import classes from "./QuoteDetailsTable.module.less";
import classNames from "classnames";
import { Fragment, type ReactNode, useMemo } from "react";
import { Card } from "~/Configurators/Linear/components/Card";
import { useTranslation } from "react-i18next";
import { QuotePartsTable } from "~/OrdersQuotes/common/components/v2/QuoteTable/QuotePartsTable";
import { ProductHeader } from "~/OrdersQuotes/common/components/v2/QuoteTable/ProductHeader";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { LinkButton } from "~/common/components/Link";
import { ToggleIcon } from "~/common/components/icon/ToggleIcon";
import type { GroupedQuoteLines, RenderCboxFn } from "~/OrdersQuotes/common/components/v2/QuoteTable/types";
import {
  makeCountableParam,
  useLineNoOffset,
  useSectionSpan,
} from "~/OrdersQuotes/common/components/v2/QuoteTable/useLineNoOffsetCalculation";
import { groupByMainQuoteLine, splitAlternatives } from "~/OrdersQuotes/common/components/v2/QuoteTable/utils";
import type { QuoteSelectionStateProps } from "~/OrdersQuotes/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";

interface QuoteTableProps extends Partial<QuoteSelectionStateProps> {
  quoteLines: QuoteLineMO[];
  className?: string;
  showCheckboxes: boolean;
  classes?: {
    headerCheckbox?: string;
    lineItemCheckbox?: string;
  };
}

const HeaderCell = ({ title, subtitle, children }: { title?: string; subtitle?: string; children?: ReactNode }) => {
  return (
    <th className={classes.headerCell}>
      {title && <span>{title}</span>}
      {subtitle && <span className={classes.subtitle}>{subtitle}</span>}
      {children}
    </th>
  );
};

export const QuoteDetailsTable = ({
  quoteLines,
  className,
  classes: { headerCheckbox: headerCheckboxClass, lineItemCheckbox: lineItemCheckboxClass } = {},
  ...rest
}: QuoteTableProps) => {
  const { t } = useTranslation();
  const groupedLines = useMemo(() => groupByMainQuoteLine(quoteLines), [quoteLines]);

  let renderQuoteLineCheckbox: RenderCboxFn = () => null;
  let headerCheckbox: ReactNode = null;
  if (rest.showCheckboxes) {
    const { isAllSelected, isSomeSelected, isAllDisabled, selectedLineNos, toggleAllLines, toggleLine } =
      rest as QuoteSelectionStateProps;
    if (rest.toggleLine) {
      renderQuoteLineCheckbox = (quoteLine: QuoteLineMO) => (
        <Checkbox
          className={lineItemCheckboxClass}
          disabled={quoteLine.disabled}
          checked={selectedLineNos[quoteLine.lineNo] ?? false}
          onCheck={() => toggleLine(quoteLine.lineNo)}
        />
      );
    }
    if (rest.toggleAllLines) {
      headerCheckbox = (
        <Checkbox
          className={headerCheckboxClass}
          disabled={isAllDisabled}
          checked={isSomeSelected}
          indeterminate={isSomeSelected && !isAllSelected}
          onCheck={toggleAllLines}
        />
      );
    }
  }

  const header = (suffix: string) => t(`quoteDetailsPage.table.header.${suffix}`);
  return (
    <table className={classNames(classes.table, className)}>
      <thead>
        <HeaderCell title={header("lineNo")} />
        <HeaderCell title={header("product")} />
        <HeaderCell title={header("quantityConfirmed")} subtitle={t("quoteListPage.table.header.requestedQuantity")} />
        <HeaderCell
          title={t("quoteDetailsPage.summary.packagingCode")}
          subtitle={t("cartPage.table.header.packaging.subtitle")}
        />
        <HeaderCell
          title={t("quoteListPage.table.header.estimatedDeliveryDate")}
          subtitle={t("cartPage.table.header.delivery.title")}
        />
        <HeaderCell title={t("order.report.header.weight")} subtitle={t("orderDetailsPage.labels.weightUnit")} />
        <HeaderCell
          title={t("quoteListPage.table.header.netPrice")}
          subtitle={t("wishlistPage.table.header.priceUnit")}
        />
        <HeaderCell title={header("total")} />
        <HeaderCell title={t("quoteListPage.table.header.lineItemStatus")} />
        <HeaderCell>{headerCheckbox}</HeaderCell>
      </thead>

      {groupedLines.map((group) => (
        <Fragment key={group.mainQuoteLine.lineNo}>
          {group.alternativeQuoteLines.length === 0 && (
            <SimpleQuoteRow quoteLine={group.mainQuoteLine} renderCheckbox={renderQuoteLineCheckbox} />
          )}
          {group.alternativeQuoteLines.length > 0 && (
            <AdvancedQuoteRow {...group} renderCheckbox={renderQuoteLineCheckbox} />
          )}
        </Fragment>
      ))}
    </table>
  );
};

const SimpleQuoteRow = ({ quoteLine, renderCheckbox }: { quoteLine: QuoteLineMO; renderCheckbox: RenderCboxFn }) => {
  const { makeOffsetStyle, setRef } = useLineNoOffset();
  return (
    <tbody>
      <tr ref={setRef}>
        <td className={classes.lineNo} {...makeOffsetStyle(quoteLine.lineNo)}>
          {quoteLine.lineNo}
        </td>
        <Card className={classes.card} style={{ gridRowEnd: `span ${quoteLine.parts.length > 1 ? 2 : 1}` }}>
          <ProductHeader
            quoteLine={quoteLine}
            includeQuoteLine={quoteLine.parts.length < 2}
            renderCheckbox={renderCheckbox}
          />
          <QuotePartsTable quoteLine={quoteLine} />
        </Card>
      </tr>
    </tbody>
  );
};

const AdvancedQuoteRow = (props: GroupedQuoteLines & { renderCheckbox: RenderCboxFn }) => {
  const { mainQuoteLine, sameProductAlternatives, differentProductAlternatives } = splitAlternatives(props);
  const { val: isAlternativeExpanded, toggle: toggleAlternative } = useBooleanState(false);
  const { val: isAlternativeProductsExpanded, toggle: toggleAlternativeProducts } = useBooleanState(false);

  const renderCheckbox = props.renderCheckbox;

  const { makeOffsetStyle, setRef } = useLineNoOffset();
  const renderQuoteLineNo = (quoteLine: QuoteLineMO) => (
    <tr key={quoteLine.lineNo}>
      <td className={classNames(classes.lineNo, classes.lineNoAdvanced)} {...makeOffsetStyle(quoteLine.lineNo)}>
        {quoteLine.lineNo}
      </td>
    </tr>
  );

  const isAnyExpanded = isAlternativeExpanded || isAlternativeProductsExpanded;

  const { sectionProps: mainSectionProps, span: mainRowspan } = useSectionSpan();
  mainSectionProps.className = classNames(classes.mainLineSection, isAnyExpanded && classes.showBar);

  const { sectionProps: altSectionProps, span: alternativeRowspan } = useSectionSpan();
  altSectionProps.className = classNames(classes.alternativeSection, isAlternativeExpanded && classes.showBar);

  const { sectionProps: altProductSectionProps, span: alternativeProductRowspan } = useSectionSpan();
  altProductSectionProps.className = classNames(
    classes.alternativeDifferentProductSection,
    isAlternativeProductsExpanded && classes.showBar
  );

  const totalRowspan = mainRowspan + alternativeRowspan + alternativeProductRowspan;

  return (
    <tbody>
      <tr ref={setRef}>
        <Card className={classNames(classes.card, classes.cardAdvanced)} style={{ gridRowEnd: `span ${totalRowspan}` }}>
          <div {...mainSectionProps}>
            <ProductHeader quoteLine={mainQuoteLine} includeQuoteLine={false} />
            <QuotePartsTable quoteLine={mainQuoteLine} forceRender renderCheckbox={renderCheckbox} />
          </div>

          {sameProductAlternatives.length > 0 && (
            <div {...altSectionProps}>
              <LinkButton
                className={classes.expandToggle}
                onClick={toggleAlternative}
                iconBefore={<ToggleIcon isActive={isAlternativeExpanded} />}
                {...makeCountableParam()}
              >
                {isAlternativeExpanded ? "Hide" : "Show"} alternative quote lines
              </LinkButton>
              {sameProductAlternatives.map((quoteLine) => (
                <QuotePartsTable
                  quoteLine={quoteLine}
                  variant={"alternative"}
                  renderCheckbox={renderCheckbox}
                  hidden={!isAlternativeExpanded}
                />
              ))}
            </div>
          )}

          {differentProductAlternatives.length > 0 && (
            <div {...altProductSectionProps}>
              <LinkButton
                className={classes.expandToggle}
                onClick={toggleAlternativeProducts}
                iconBefore={<ToggleIcon isActive={isAlternativeProductsExpanded} />}
                {...makeCountableParam()}
              >
                {isAlternativeProductsExpanded ? "Hide" : "Show"} alternative product(s)
              </LinkButton>
              {differentProductAlternatives.map(({ quoteLines }) => (
                <>
                  <ProductHeader
                    quoteLine={quoteLines[0]}
                    includeQuoteLine={false}
                    hidden={!isAlternativeProductsExpanded}
                    alternative={true}
                  />
                  {quoteLines.map((quoteLine) => (
                    <QuotePartsTable
                      quoteLine={quoteLine}
                      variant={"alternative"}
                      renderCheckbox={renderCheckbox}
                      hidden={!isAlternativeProductsExpanded}
                    />
                  ))}
                </>
              ))}
            </div>
          )}
        </Card>
      </tr>
      {renderQuoteLineNo(mainQuoteLine)}

      {isAlternativeExpanded && sameProductAlternatives.map(renderQuoteLineNo)}
      {isAlternativeProductsExpanded &&
        differentProductAlternatives.flatMap(({ quoteLines }) => quoteLines).map(renderQuoteLineNo)}
    </tbody>
  );
};
